import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { X, Calendar, User, Mail, Phone, CheckCircle, MessageCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import { useBooking } from '../contexts/BookingContext'
import { BookingForm, Schedule, apiService } from '../core'
import { getClasses } from '../data'
import { getDisplayDateIST, formatTimeRangeWithAMPM } from '../core'
import InlineDateCalendar from './InlineDateCalendar'
import TimeSlotSelector from './TimeSlotSelector'
// Error handling is done inline with toast notifications

const bookingSchema = z.object({
  classId: z.number().min(1, 'Please select a class'),
  studentName: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email'),
  phone: z.string().min(10, 'Please enter a valid phone number'),
  preferredDate: z.string().optional(),
  preferredTime: z.string().optional(),
})

export default function BookingModal() {
  const { isBookingOpen, setIsBookingOpen, selectedClass, setSelectedClass, isDemo } = useBooking()
  // Error handling is now done inline
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null)
  const [selectedDate, setSelectedDate] = useState<string>('')
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const previousClassIdRef = useRef<number | null>(null)
  
  
  const classes = getClasses()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
  } = useForm<BookingForm>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {},
  })

  const watchedClassId = watch('classId')

  // Set selected class when modal opens
  useEffect(() => {
    if (selectedClass && isBookingOpen) {
      setValue('classId', selectedClass.id)
    }
  }, [selectedClass, isBookingOpen, setValue])

  // Clear date and time when class changes (but not on initial load)
  useEffect(() => {
    if (watchedClassId && isBookingOpen && previousClassIdRef.current !== null && previousClassIdRef.current !== watchedClassId) {
      // Clear date and time selections when class changes
      setSelectedDate('')
      setSelectedSchedule(null)
      setIsCalendarOpen(false)
    }
    // Update the ref with current class ID
    previousClassIdRef.current = watchedClassId
  }, [watchedClassId, isBookingOpen])

  // Reset form state when modal opens
  useEffect(() => {
    if (isBookingOpen) {
      setSelectedDate('')
      setSelectedSchedule(null)
      setIsCalendarOpen(false)
    }
  }, [isBookingOpen])

  const handleDateSelect = (date: string) => {
    setSelectedDate(date)
    setSelectedSchedule(null)
    setIsCalendarOpen(false)
  }

  const handleScheduleSelect = (schedule: Schedule) => {
    setSelectedSchedule(schedule)
  }

  const onSubmit = async (data: BookingForm) => {
    if (!selectedDate || !selectedSchedule) {
      toast.error('Please select both date and time slot')
      return
    }


    setIsProcessing(true)

    try {
      const selectedClassData = classes?.find(c => c.id === data.classId)
      if (!selectedClassData) {
        toast.error('Selected class is no longer available')
        return
      }

      // Prepare enrollment data (simplified with sessionId)
      const enrollmentData = {
        studentName: data.studentName,
        studentEmail: data.email,
        studentPhone: data.phone,
        sessionId: selectedSchedule.id // Use session ID directly
      }

      // Store enrollment to database and Google Sheets
      const result = await apiService.createEnrollment(enrollmentData)
      
      // Add enrollment ID and session details to data
      const enrollmentDataWithId = {
        ...enrollmentData,
        enrollmentId: result.enrollment_id || `enroll_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        // Add session details for success page display
        className: selectedClassData.name,
        selectedDate,
        selectedTime: selectedSchedule.startTime,
        isDemo: Boolean(isDemo)
      }


      // Store enrollment data for success page
      sessionStorage.setItem('enrollmentData', JSON.stringify(enrollmentDataWithId))

      // Show success message
      const message = isDemo 
        ? 'Demo session requested successfully! We will contact you soon to confirm your booking.'
        : 'Class enrollment requested successfully! We will contact you soon to confirm your booking and payment details.'
      
      toast.success(message)

      // Redirect to success page
      window.location.href = '/success'

    } catch (error) {
      // Show the specific error message if available
      const errorMessage = error instanceof Error ? error.message : 'Failed to process booking. Please try again.'
      toast.error(errorMessage)
    } finally {
      setIsProcessing(false)
    }
  }


  const handleClose = () => {
    reset()
    setSelectedClass(null)
    setSelectedSchedule(null)
    setSelectedDate('')
    setIsCalendarOpen(false)
    setIsBookingOpen(false)
    previousClassIdRef.current = null
    
  }

  const selectedClassData = classes?.find(c => c.id === watchedClassId)
  

  if (!isBookingOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-800 rounded-lg shadow-xl mx-2 sm:mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-700/20 dark:to-secondary-700/20 p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Calendar className="w-6 h-6 text-primary-600" />
                  {isDemo ? 'Book Free Demo Session' : 'Book Your Dance Class'}
                </h2>
                <p className="text-gray-600 dark:text-gray-300 mt-1">
                  {isDemo ? 'Try your first class with Dr. Sophia Mohan - completely free!' : 'Start your journey with Dr. Sophia Mohan'}
                </p>
              </div>
              <button
                onClick={handleClose}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="h-5 w-5 text-gray-600 dark:text-gray-300" />
              </button>
            </div>
          </div>

          {/* Form */}
          <div className="p-6">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Class Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Select Class *</label>
                <select
                  {...register('classId', { valueAsNumber: true })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Choose your dance class</option>
                  {classes.map((classItem) => (
                    <option key={classItem.id} value={classItem.id}>
                      {classItem.name} ({classItem.level})
                    </option>
                  ))}
                </select>
                {errors.classId && (
                  <p className="text-sm text-red-600">{errors.classId.message}</p>
                )}
                
                {selectedClassData && (
                  <div className="mt-2 p-3 bg-primary-50 dark:bg-primary-700/20 rounded-lg">
                    <p className="text-sm text-primary-800 dark:text-primary-200">
                      <strong>{selectedClassData.name}</strong> - {selectedClassData.description}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-primary-600 dark:text-primary-400">
                      <span>Duration: {selectedClassData.duration} min</span>
                      {/* <span>Max Capacity: {selectedClassData.maxCapacity} students</span> */}
                      <span className="font-semibold text-blue-600">
                        Type: {isDemo ? 'FREE Demo Session' : 'Regular Class'}
                      </span>
                      {!isDemo && (
                        <span className="font-semibold text-blue-600 dark:text-blue-400">
                          We will contact you for payment and further details
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Date Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Preferred Date *</label>
                <button
                  type="button"
                  onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-left flex items-center gap-2"
                >
                  <Calendar className="w-4 h-4" />
                  {selectedDate ? getDisplayDateIST(selectedDate) : 'Select a date'}
                </button>
                {isCalendarOpen && watchedClassId && (
                  <InlineDateCalendar
                    danceClass={selectedClassData || null}
                    selectedDate={selectedDate}
                    onDateSelect={handleDateSelect}
                    isOpen={isCalendarOpen}
                    onToggle={() => setIsCalendarOpen(!isCalendarOpen)}
                    isDemo={isDemo}
                  />
                )}
              </div>

              {/* Time Slot Selection */}
              {selectedDate && watchedClassId && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Available Time Slots *</label>
                  <TimeSlotSelector
                    danceClass={selectedClassData || null}
                    selectedDate={selectedDate}
                    selectedSchedule={selectedSchedule}
                    onScheduleSelect={handleScheduleSelect}
                    isDemo={isDemo}
                  />
                </div>
              )}

              {/* Student Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Full Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      {...register('studentName')}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Enter your full name"
                    />
                  </div>
                  {errors.studentName && (
                    <p className="text-sm text-red-600">{errors.studentName.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      {...register('email')}
                      type="email"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Enter your email"
                    />
                  </div>
                  {errors.email && (
                    <p className="text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Phone Number *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    {...register('phone')}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Enter your phone number"
                  />
                </div>
                {errors.phone && (
                  <p className="text-sm text-red-600">{errors.phone.message}</p>
                )}
              </div>

              {/* Simplified form - removed experience and special requests fields */}

              {/* Selected Schedule Summary */}
              {selectedSchedule && selectedDate && (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">Selected Schedule:</h4>
                  <div className="text-sm text-green-700 dark:text-green-300">
                    <p><strong>Date:</strong> {getDisplayDateIST(selectedDate)}</p>
                    <p><strong>Time:</strong> {formatTimeRangeWithAMPM(selectedSchedule.startTime, selectedSchedule.endTime)}</p>
                    <p><strong>Type:</strong> {selectedSchedule.isDemo ? 'Free Demo Session' : 'Regular Class'}</p>
                    <p><strong>Availability:</strong> 
                      <span className={selectedSchedule.isFullyBooked ? 'text-red-600 font-semibold' : 'text-green-700 dark:text-green-300'}>
                        {selectedSchedule.isFullyBooked 
                          ? 'Fully Booked' 
                          : `${selectedSchedule.remainingSpots || 0} spots remaining`
                        }
                      </span>
                    </p>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || isProcessing || !selectedDate || !selectedSchedule || selectedSchedule?.isFullyBooked}
                  className="flex-1 btn btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Processing...
                    </>
                  ) : selectedSchedule?.isFullyBooked ? (
                    <>
                      <X className="w-4 h-4" />
                      Fully Booked
                    </>
                  ) : isDemo ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Request Free Demo
                    </>
                  ) : (
                    <>
                      <MessageCircle className="w-4 h-4" />
                      Request Enrollment
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}